import { Suspense, lazy, useState } from 'react';
import {
    Button, Menu, MenuItem, MenuList, Divider, ListSubheader,
    ListItemIcon, ListItemText, Dialog, DialogContent, DialogTitle,
    DialogActions, Alert, TextField,
    alpha,
    Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import DifferenceIcon from '@mui/icons-material/Difference';
import HistoryIcon from '@mui/icons-material/History';
import VerifiedIcon from '@mui/icons-material/Verified';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Localized } from '@fluent/react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { DocumentReportPrinter, PrinterMode } from './DocumentReportPrinter';
import { DocumentNewVersionDialog } from './DocumentNewVersionDialog';
import { DocumentExcelExporter } from './DocumentExcelExporter';
import { GreenExcelIcon } from '../../../app/customIcons/ExcelIcon';
import { DocumentSoftDeleteDialog } from './DocumentSoftDeleteDialog';
import { CreateDocumentCopyDialog } from './CreateDocumentCopyDialog';
import { UrsDiff } from '../../urs/UrsDiff';
import { CompanyFeatureFlags, useHasFeatureFlag } from '../../settings/featureFlags';
import TuneIcon from '@mui/icons-material/Tune';
import { DocumentSysUpdateDialog } from './DocumentSysUpdateDialog';
import { DocumentOwnerDialog } from './DocumentOwnerDialog';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { postWithAuth } from '../../../http';
import { createTemplateFromUrs } from '../../urstemplates/ursTemplatesSlice';
import { loadDocument, loadDocumentActions } from '../documentsApi';
import { downloadUrsReport } from '../../urs/UrsSlice';
import { usePackage } from "../../permissions/usePackage";

// Loaded only once Compare is opened, so neither the compare view nor node-htmldiff reaches
// the main bundle for the sessions that never use it. Must stay a lazy import in this file:
// naming any symbol from the module eagerly would pull the whole chunk back into main.js.
const CompareSnapshotDialog = lazy(() => import('../compare/CompareSnapshotDialog')
    .then(m => ({ default: m.CompareSnapshotDialog })));

export const UrsDocumentToolbarMenu = ({ documentId, documentNo, documentTitle, language, structureId, advancedWFL }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [mode, setMode] = useState<PrinterMode>('download');
    const [commented, setCommented] = useState(false);
    const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
    const [documentSysUpdateDialog, setDocumentSysUpdateDialog] = useState(false);
    const [exportToExcel, setExportToExcel] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const [documentOwnerDialogOpen, setDocumentOwnerDialogOpen] = useState(false);
    const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);
    const canWriteTemplates = usePackage("UrsTemplates", "ReadWrite");
    const [cancelDocumentDialogOpen, setCancelDocumentDialogOpen] = useState(false);
    const [newTemplateNumber, setNewTemplateNumber] = useState('');
    const [newTemplateDocDesignation, setNewTemplateDocDesignation] = useState('');
    const [cancelPassword, setCancelPassword] = useState('');
    const [creatingTemplate, setCreatingTemplate] = useState(false);
    const [cancelingDocument, setCancelingDocument] = useState(false);
    const [templateServerError, setTemplateServerError] = useState<string | null>(null);
    const [cancelServerError, setCancelServerError] = useState<string | null>(null);
    const hasDocumentSysUpdate = useHasFeatureFlag(CompanyFeatureFlags.DocumentSysUpdate);
    const [generatingDocx, setGeneratingDocx] = useState(false);

    const open = Boolean(anchorEl);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleGenerateDocxClick = async () => {
        handleClose();
        setGeneratingDocx(true);
        try {
            const result = await dispatch(downloadUrsReport(documentId));
            if (downloadUrsReport.fulfilled.match(result)) {
                const link = document.createElement('a');
                link.href = result.payload.blobUrl;
                link.setAttribute('download', result.payload.fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                dispatch(showError('error'));
            }
        } finally {
            setGeneratingDocx(false);
        }
    };

    const handleMenuItemClick = (selectedMode: PrinterMode, isCommented = false) => {
        setMode(selectedMode);
        setCommented(isCommented);
        setIsDialogOpen(true);
        handleClose();
    };
    const handleNewVersionClick = () => {
        setNewVersionDialogOpen(true);
        handleClose();
    };
    const hadleExportToExcelClick = () => {
        setExportToExcel(true);
        handleClose();
    };
    const handleCloseExportToExcel = () => {
        setExportToExcel(false);
    };
    const handleConsistencyClick = () => {
        navigate(`/urs/consistency-validator/${documentId}`);
        handleClose();
    };
    const handleHistoryClick = () => {
        setHistoryDialogOpen(true);
        handleClose();
    };
    const handleCompareClick = () => {
        setCompareDialogOpen(true);
        handleClose();
    };
    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
        handleClose();
    };
    const handleCopyDocumentClick = () => {
        setCopyDialogOpen(true);
        handleClose();
    };
    const handleDocumentSysUpdateClick = () => {
        setDocumentSysUpdateDialog(true);
        handleClose();
    };

    const handleDocumentOwnerClick = () => {
        setDocumentOwnerDialogOpen(true);
        handleClose();
    };

    const handleCreateTemplateClick = () => {
        setTemplateServerError(null);
        setCreateTemplateDialogOpen(true);
        handleClose();
    };

    const handleCancelDocumentClick = () => {
        setCancelServerError(null);
        setCancelDocumentDialogOpen(true);
        handleClose();
    };

    const handleCreateTemplateConfirm = async () => {
        if (!newTemplateDocDesignation.trim() || creatingTemplate) {
            return;
        }

        setCreatingTemplate(true);
        setTemplateServerError(null);

        try {
            const result = await dispatch(createTemplateFromUrs({
                ursId: String(documentId),
                templateNo: newTemplateNumber.trim(),
                description: newTemplateDocDesignation.trim(),
            }));

            if (createTemplateFromUrs.fulfilled.match(result)) {
                dispatch(showSuccess('saved'));
                setCreateTemplateDialogOpen(false);
                setNewTemplateNumber('');
                setNewTemplateDocDesignation('');
            } else {
                let errorMsg = 'error';
                if (result.payload?.kind === 'http') {
                    const problem = result.payload.problem;
                    if (problem?.title) {
                        errorMsg = problem.title;
                    }
                }
                setTemplateServerError(errorMsg);
            }
        } catch {
            dispatch(showError('error'));
        } finally {
            setCreatingTemplate(false);
        }
    };

    const handleCancelDocumentConfirm = async () => {
        if (cancelingDocument) {
            return;
        }

        setCancelingDocument(true);
        setCancelServerError(null);

        try {
            const result = await dispatch(postWithAuth({
                url: `api/document-actions/${documentId}/runaction`,
                payload: {
                    action: 'CancelDocument',
                    password: cancelPassword,
                },
            }));

            if (postWithAuth.fulfilled.match(result)) {
                dispatch(showSuccess('saved'));
                setCancelDocumentDialogOpen(false);
                setCancelPassword('');
                await dispatch(loadDocument(String(documentId)));
                await dispatch(loadDocumentActions(String(documentId)));
            } else {
                let errorMsg = 'error';
                if (result.payload?.kind === 'http') {
                    const problem = result.payload.problem;
                    if (problem?.title) {
                        errorMsg = problem.title;
                    }
                }
                setCancelServerError(errorMsg);
            }
        } catch {
            dispatch(showError('error'));
        } finally {
            setCancelingDocument(false);
        }
    };

    return (
        <>
            <Button
                aria-controls={open ? 'document-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="contained"
                color="primary"
                size="small"
                endIcon={<ArrowDropDownIcon />}
            >
                <Localized id="document"><span>_Document</span></Localized>
            </Button>

            <Menu
                id="document-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: { sx: { minWidth: 280, py: 0 } }
                }}
            >
                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Wydruki
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem onClick={() => handleMenuItemClick('open')} >
                        <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="print"><span>_Open report</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick('open', true)}>
                        <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="print-commented"><span>_Wydruk skomentowany</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick('download')}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="report-download"><span>_Download report</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleGenerateDocxClick} disabled={generatingDocx}>
                        <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="report-generate-docx"><span>Generate docx</span></Localized>} />
                    </MenuItem>

                </MenuList>

                <Divider sx={{ my: 0.5 }} />

                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Document
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem onClick={handleNewVersionClick}>
                        <ListItemIcon><DifferenceIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-create-new-version"><span>_Nowa wersja</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleCopyDocumentClick}>
                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="urs-create-copy">Utwórz kopię URS</Localized>} />
                    </MenuItem>
                    {canWriteTemplates && <MenuItem onClick={handleCreateTemplateClick}>
                        <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-save-as-template"><span>_Zapisz jako szablon</span></Localized>} />
                    </MenuItem>}
                    <MenuItem onClick={handleCancelDocumentClick}>
                        <ListItemIcon><CancelOutlinedIcon fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-action-cancel"><span>_Anuluj dokument</span></Localized>} />
                    </MenuItem>
                    {/* <MenuItem onClick={handleHistoryClick}>
                        <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-history"><span>_Historia dokumentu</span></Localized>} />
                    </MenuItem> */}
                    <MenuItem onClick={handleCompareClick}>
                        <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-compare-action"><span>_Porównaj</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText primary={<Localized id="delete"><span>_Usuń</span></Localized>} />
                    </MenuItem>
                </MenuList >

                <Divider sx={{ my: 0.5 }} />

                <MenuList
                    dense
                    subheader={
                        <ListSubheader
                            disableSticky
                            sx={(t) => ({
                                typography: 'overline',
                                letterSpacing: 0.5,
                                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.20 : 0.12),
                                color: t.palette.primary.main,
                                fontSize: 12,
                                lineHeight: 1.6,
                                py: 0.75,
                            })}
                        >
                            Akcje
                        </ListSubheader>
                    }
                    sx={{ p: 0 }}
                >
                    <MenuItem onClick={handleDocumentOwnerClick}>
                        <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-action-change-owner"><span>_Zmień właściciela dokumentu</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={handleConsistencyClick}>
                        <ListItemIcon><VerifiedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-validate"><span>_Waliduj dokument</span></Localized>} />
                    </MenuItem>
                    <MenuItem onClick={hadleExportToExcelClick}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Box sx={{
                                display: 'flex',
                                [`& svg`]: {
                                    width: '20px',
                                    height: '20px',
                                },
                            }}>
                                <GreenExcelIcon />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primary={<Localized id='export-to-excel'>_Eksportuj do Excela</Localized>} />
                    </MenuItem>
                    {hasDocumentSysUpdate && (<MenuItem onClick={handleDocumentSysUpdateClick}>
                        <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={<Localized id="document-sys-update-action"><span>_Wymuś zmiany w dokumencie</span></Localized>} />
                    </MenuItem>)}

                </MenuList>
            </Menu >

            <DocumentReportPrinter
                documentId={documentId}
                language=""
                mode={mode}
                commented={commented}
                isDialogOpen={isDialogOpen}
                closeDialog={() => setIsDialogOpen(false)}
            />

            {
                documentOwnerDialogOpen && (
                    <DocumentOwnerDialog
                        documentId={documentId}
                        advancedWFL={advancedWFL}
                        onSuccess={() => setDocumentOwnerDialogOpen(false)}
                        onCancel={() => setDocumentOwnerDialogOpen(false)}
                    />
                )
            }

            {
                documentSysUpdateDialog && (
                    <DocumentSysUpdateDialog
                        documentId={documentId}
                        onSuccess={() => setDocumentSysUpdateDialog(false)}
                        onCancel={() => setDocumentSysUpdateDialog(false)}
                    />
                )
            }

            {
                newVersionDialogOpen && (
                    <DocumentNewVersionDialog
                        documentId={documentId}
                        onSuccess={() => setNewVersionDialogOpen(false)}
                        onCancel={() => setNewVersionDialogOpen(false)}
                    />
                )
            }

            {exportToExcel && <DocumentExcelExporter
                documentId={documentId}
                language=""
                mode={"open"}
                isDialogOpen={true}
                closeDialog={handleCloseExportToExcel}
            />
            }

            <DocumentSoftDeleteDialog
                open={deleteDialogOpen}
                documentId={String(documentId)}
                documentNo={documentNo}
                onClose={() => setDeleteDialogOpen(false)}
            />

            <CreateDocumentCopyDialog
                open={copyDialogOpen}
                onClose={() => setCopyDialogOpen(false)}
                documentId={documentId}
                sourceDocumentNo={documentNo}
                sourceDocumentTitle={documentTitle}
                defaultLanguage={language}
                defaultStructureId={structureId}
            />

            <Dialog
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle>
                    <Localized id="document-history"><span>_Historia dokumentu</span></Localized>
                </DialogTitle>
                <DialogContent dividers sx={{ minHeight: '70vh' }}>
                    {historyDialogOpen && <UrsDiff ursId={documentId} />}
                </DialogContent>
            </Dialog>

            <Dialog
                open={createTemplateDialogOpen}
                onClose={() => setCreateTemplateDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ bgcolor: 'grey.100' }}>
                    <Localized id="document-template-create"><span>_Utwórz szablon</span></Localized>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" variant="outlined">
                        <Localized id="feature-under-development">
                            <span>_Sorry, this feature is under development and will be available soon.</span>
                        </Localized>
                    </Alert>
                    <TextField
                        sx={{ mt: 2 }}
                        value={newTemplateNumber}
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) => setNewTemplateNumber(e.target.value)}
                        label={<Localized id="urs-template-no"><span>_Numer szablonu</span></Localized>}
                    />
                    <TextField
                        sx={{ mt: 2 }}
                        value={newTemplateDocDesignation}
                        fullWidth
                        inputProps={{ maxLength: 2000 }}
                        multiline
                        minRows={4}
                        maxRows={4}
                        onChange={(e) => setNewTemplateDocDesignation(e.target.value)}
                        label={<Localized id="urs-template-description"><span>_Opis</span></Localized>}
                    />
                    {templateServerError && (
                        <Alert sx={{ mt: 2 }} onClose={() => setTemplateServerError(null)} severity="error">
                            <Localized id={templateServerError}>
                                <span>_Server error</span>
                            </Localized>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <LoadingButton disabled={true} loading={creatingTemplate} variant="contained" onClick={handleCreateTemplateConfirm}>
                        <Localized id="save"><span>_Zapisz</span></Localized>
                    </LoadingButton>
                    <Button disabled={creatingTemplate} onClick={() => setCreateTemplateDialogOpen(false)}>
                        <Localized id="cancel"><span>_Anuluj</span></Localized>
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelDocumentDialogOpen}
                onClose={() => setCancelDocumentDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ bgcolor: 'grey.100' }}>
                    <Localized id="document-action-cancel"><span>_Anuluj dokument</span></Localized>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Localized id="document-action-cancel-question">
                            <span>_Czy na pewno chcesz anulować dokument?</span>
                        </Localized>
                    </Alert>
                    <TextField
                        sx={{ mt: 2 }}
                        fullWidth
                        type="password"
                        value={cancelPassword}
                        onChange={(e) => setCancelPassword(e.target.value)}
                        label={<Localized id="password"><span>_Hasło</span></Localized>}
                        autoComplete="off"
                    />
                    {cancelServerError && (
                        <Alert sx={{ mt: 2 }} onClose={() => setCancelServerError(null)} severity="error">
                            <Localized id={cancelServerError}>
                                <span>_Server error</span>
                            </Localized>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={cancelingDocument} disabled={cancelingDocument || !cancelPassword.trim()} variant="contained" onClick={handleCancelDocumentConfirm}>
                        <Localized id="yes"><span>_Tak</span></Localized>
                    </LoadingButton>
                    <Button disabled={cancelingDocument} onClick={() => setCancelDocumentDialogOpen(false)}>
                        <Localized id="cancel"><span>_Anuluj</span></Localized>
                    </Button>
                </DialogActions>
            </Dialog>
            {
                compareDialogOpen && (
                    <Suspense fallback={null}>
                        <CompareSnapshotDialog
                            documentId={documentId}
                            onCancel={() => setCompareDialogOpen(false)}
                        />
                    </Suspense>
                )
            }
        </>
    );
};
